import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { createAttraction, updateAttraction } from "@/lib/attractionsApi";
import { useToast } from "@/hooks/use-toast";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { NumericInput } from "@/components/ui/numeric-input";
import { MoneyInput } from "@/components/ui/money-input";
import { formatErrorMessage } from "@/lib/errorMessages";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const formSchema = z.object({
  attraction_code: z.string().min(1, "Attraction code is required"),
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  image_url: z.string().optional(),
  supplier_name: z.string().optional(),
  tax_percentage: z.string().optional(),
  opening_hours: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

type Ticket = {
  id?: number;
  title: string;
  description: string;
  pricing_type: "per_person" | "per_family" | "per_group";
  cost_supplier: string;
  website_price: string;
  price_op: string;
  price_ag: string;
  price_cf: string;
};

interface AttractionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any;
  onSuccess: () => void;
}

const AttractionForm = ({ open, onOpenChange, initialData, onSuccess }: AttractionFormProps) => {
  const { toast } = useToast();
  const [tickets, setTickets] = useState<Ticket[]>([
    {
      id: undefined,
      title: "",
      description: "",
      pricing_type: "per_person",
      cost_supplier: "",
      website_price: "",
      price_op: "",
      price_ag: "",
      price_cf: "",
    },
  ]);
  const [expandedTickets, setExpandedTickets] = useState<Record<number, boolean>>({ 0: true });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      attraction_code: "",
      title: "",
      description: "",
      image_url: "",
      supplier_name: "",
      tax_percentage: "",
      opening_hours: "",
    },
  });

  const {
    handleOpenChange: handleUnsavedOpenChange,
    ConfirmDialog,
    pendingClose,
    setPendingClose,
  } = useUnsavedChanges({
    isDirty: form.formState.isDirty,
    onSave: () => form.handleSubmit(onSubmit)(),
    onDiscard: () => {
      form.reset();
      setTickets([
        {
          title: "",
          description: "",
          pricing_type: "per_person",
          cost_supplier: "",
          website_price: "",
          price_op: "",
          price_ag: "",
          price_cf: "",
        },
      ]);
      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (pendingClose) {
      onOpenChange(false);
      setPendingClose(false);
    }
  }, [pendingClose, onOpenChange, setPendingClose]);

  useEffect(() => {
    if (initialData) {
      form.reset({
        attraction_code: initialData.attraction_code || "",
        title: initialData.title || "",
        description: initialData.description || "",
        image_url: initialData.image_url || "",
        supplier_name: initialData.supplier_name || "",
        tax_percentage: initialData.tax_percentage?.toString() || "",
        opening_hours: initialData.opening_hours || "",
      });
      if (initialData.tickets && initialData.tickets.length > 0) {
        setTickets(
          initialData.tickets.map((ticket: any) => ({
            id: ticket.id,
            title: ticket.title || "",
            description: ticket.description || "",
            pricing_type: ticket.pricing_type || "per_person",
            cost_supplier: ticket.cost_supplier?.toString() || "",
            website_price: ticket.website_price?.toString() || "",
            price_op: ticket.price_op?.toString() || "",
            price_ag: ticket.price_ag?.toString() || "",
            price_cf: ticket.price_cf?.toString() || "",
          }))
        );
      }
    } else {
      form.reset();
      setTickets([
        {
          id: undefined,
          title: "",
          description: "",
          pricing_type: "per_person",
          cost_supplier: "",
          website_price: "",
          price_op: "",
          price_ag: "",
          price_cf: "",
        },
      ]);
      setExpandedTickets({ 0: true });
    }
  }, [initialData, form]);

  const addTicket = () => {
    setTickets([
      {
        id: undefined,
        title: "",
        description: "",
        pricing_type: "per_person",
        cost_supplier: "",
        website_price: "",
        price_op: "",
        price_ag: "",
        price_cf: "",
      },
      ...tickets,
    ]);
    
    setExpandedTickets((prev) => {
      const newExpanded: Record<number, boolean> = { 0: true };
      Object.keys(prev).forEach((key) => {
        const index = parseInt(key);
        newExpanded[index + 1] = prev[index];
      });
      return newExpanded;
    });

    setTimeout(() => {
      const ticketsSection = document.querySelector('[data-tickets-section]');
      if (ticketsSection) {
        ticketsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const removeTicket = (index: number) => {
    setTickets(tickets.filter((_, i) => i !== index));
    setExpandedTickets((prev) => {
      const newExpanded: Record<number, boolean> = {};
      Object.keys(prev).forEach((key) => {
        const i = parseInt(key);
        if (i < index) {
          newExpanded[i] = prev[i];
        } else if (i > index) {
          newExpanded[i - 1] = prev[i];
        }
      });
      return newExpanded;
    });
  };

  const updateTicket = (index: number, field: keyof Ticket, value: string) => {
    const newTickets = [...tickets];
    newTickets[index][field] = value as any;
    setTickets(newTickets);
  };

  const parseNumber = (value: string): number | null => {
    const trimmed = value?.trim();
    if (!trimmed) return null;
    const num = Number(trimmed);
    return Number.isNaN(num) ? null : num;
  };

  const onSubmit = async (data: FormData) => {
    try {
      const attractionPayload = {
        attraction_code: data.attraction_code,
        title: data.title,
        description: data.description || null,
        image_url: data.image_url || null,
        supplier_name: data.supplier_name || null,
        tax_percentage: data.tax_percentage ? parseFloat(data.tax_percentage) : null,
        opening_hours: data.opening_hours || null,
        tickets: tickets
          .filter((ticket) => ticket.title.trim() !== "")
          .map((ticket) => ({
            id: ticket.id,
            title: ticket.title,
            description: ticket.description || null,
            pricing_type: ticket.pricing_type,
            cost_supplier: parseNumber(ticket.cost_supplier),
            website_price: parseNumber(ticket.website_price),
            price_op: parseNumber(ticket.price_op),
            price_ag: parseNumber(ticket.price_ag),
            price_cf: parseNumber(ticket.price_cf),
          })),
      };

      if (initialData?.id) {
        await updateAttraction(initialData.id, attractionPayload);
      } else {
        await createAttraction(attractionPayload);
      }

      toast({
        title: "Success",
        description: `Attraction ${initialData ? "updated" : "created"} successfully`,
      });
      form.reset();
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: formatErrorMessage(error, "Failed to save attraction"),
        variant: "destructive",
      });
    }
  };

  const toggleTicketExpanded = (index: number) => {
    setExpandedTickets((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      const shouldClose = handleUnsavedOpenChange(open);
      if (shouldClose) {
        onOpenChange(false);
      }
    } else {
      onOpenChange(open);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initialData ? "Edit Attraction" : "Add New Attraction"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="attraction_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Attraction Code *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="image_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Image URL</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="supplier_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Supplier Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tax_percentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tax (%)</FormLabel>
                    <FormControl>
                      <NumericInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        allowDecimals={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="opening_hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opening Hours</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4 border-t pt-4" data-tickets-section>
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Tickets</h3>
                <Button type="button" size="sm" onClick={addTicket}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Ticket
                </Button>
              </div>

              {tickets.map((ticket, index) => {
                const isExpanded = expandedTickets[index];
                
                return (
                  <div key={index} className="border rounded-lg overflow-hidden">
                    <div 
                      className="p-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => toggleTicketExpanded(index)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <h4 className="font-medium">{ticket.title || "New Ticket"}</h4>
                          {!isExpanded && (
                            <p className="text-sm text-muted-foreground whitespace-nowrap overflow-x-auto">
                              {[
                                ticket.cost_supplier && `Supplier: $${ticket.cost_supplier}`,
                                ticket.website_price && `Website: $${ticket.website_price}`,
                                ticket.price_op && `OP: $${ticket.price_op}`,
                                ticket.price_ag && `AG: $${ticket.price_ag}`,
                                ticket.price_cf && `CF: $${ticket.price_cf}`
                              ].filter(Boolean).join(' | ')}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <div className="flex items-center justify-center w-8 h-8">
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </div>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTicket(index);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">Title</label>
                            <Input
                              value={ticket.title}
                              onChange={(e) => updateTicket(index, "title", e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Description</label>
                            <Textarea
                              value={ticket.description}
                              onChange={(e) => updateTicket(index, "description", e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <label className="text-sm font-medium">Cost Supplier (USD)</label>
                            <MoneyInput
                              value={ticket.cost_supplier}
                              onChange={(value) => updateTicket(index, "cost_supplier", value)}
                              className="w-[140px]"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Website Price (USD)</label>
                            <MoneyInput
                              value={ticket.website_price}
                              onChange={(value) => updateTicket(index, "website_price", value)}
                              className="w-[140px]"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Price OP (USD)</label>
                            <MoneyInput
                              value={ticket.price_op}
                              onChange={(value) => updateTicket(index, "price_op", value)}
                              className="w-[140px]"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Price AG (USD)</label>
                            <MoneyInput
                              value={ticket.price_ag}
                              onChange={(value) => updateTicket(index, "price_ag", value)}
                              className="w-[140px]"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">Price CF (USD)</label>
                            <MoneyInput
                              value={ticket.price_cf}
                              onChange={(value) => updateTicket(index, "price_cf", value)}
                              className="w-[140px]"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Pricing Type</label>
                            <Select 
                              value={ticket.pricing_type} 
                              onValueChange={(value) => updateTicket(index, "pricing_type", value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="per_person">Per person</SelectItem>
                                <SelectItem value="per_family">Per family</SelectItem>
                                <SelectItem value="per_group">Per group</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
    <ConfirmDialog />
    </>
  );
};

export default AttractionForm;
